import GenericLoader from '../../../Framework/Components/Loaders/GenericLoader';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
	SendsPageDataType,
	SendsPagesUpdateType,
	updateSendsPage,
	updateSendsPageActiveStatus,
	useChangeSendsStatus,
	useSendsPage,
	useUpdateSendsData,
} from '../../../Api/StandaloneSendsEndpoints';
import CreateStandaloneSendForm from '../../../Components/Sends/CreateStandaloneSendsForm';
import SendsAdminNavigation from '../../../Components/Sends/SendsAdminNavigation';
import FullWidthSidebar from '../../../Framework/Components/PageLayouts/FullWidthSidebar';
import BasicPageHeader from '../../../Framework/Components/LayoutScaffold/BasicPageHeader';
import { ColourMod } from '../../../Framework/Data/Enums/LayoutEnums';
import { useToasts } from '../../../Framework/Providers/ToastsProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faClipboard,
	faCopy,
	faEllipsisVertical,
	faExternalLink,
	faPencil,
	faRectangleHistory,
	faTrash,
	faWavePulse,
} from '@fortawesome/pro-solid-svg-icons';
import { useLayout } from '../../../Framework/Providers/LayoutProvider';
import Button from '../../../Framework/Components/Buttons/Button';
import DropdownMenu from '../../../Framework/Components/DropdownMenus/DropdownMenu';
import ItemMenuEntry from '../../../Framework/Components/Navigation/ItemMenuEntry';
import { ParseURL } from '../../../Framework/Constants/Helpers';
import PageHistoryModal from '../../../Components/Sends/Modals/PageHistoryModal';
import SendMailHistoryProvider from '../../../Providers/SendMailHistoryProvider';
import ConfirmDeleteSendsPageModal from '../../../Components/Sends/Modals/ConfirmDeleteSendsPageModal';
import ToggleSwitch from '../../../Framework/Components/Forms/Toggle/ToggleSwitch';
import { ToggleType } from '../../../Framework/Data/Enums/Generic';

const EditSendsPage = () => {
	const { isMedium, isLarge, isXL } = useLayout();

	const navigate = useNavigate();

	const { pageId } = useParams();
	const { addToast } = useToasts();
	const { data, status, error }: any = useSendsPage(parseInt(pageId as string));

	const [activeSendsPage, setActiveSendsPage] = useState<any>({ isOpen: false, pageData: null });

	const [pageHistoryOpen, setPageHistoryOpen] = useState<boolean>(false);

	const [savingData, setSavingData] = useState<boolean>(false);
	const [saveError, setSaveError] = useState<any>();

	const updateSendsStatus = useChangeSendsStatus();
	const sendsTemplates = useUpdateSendsData();

	const HandleSave = async (newData: SendsPageDataType, e: any) => {
		setSavingData(true);

		newData.isActive = data.isActive;

		await updateSendsPage(parseInt(pageId as string), newData)
			.then((resp: any) => {
				sendsTemplates.mutate({
					updateType: SendsPagesUpdateType.UPDATE,
					newData: { ...data, ...newData },
				});

				addToast('Sends Profile Updated!', <FontAwesomeIcon icon={faPencil} />, ColourMod.Success);
			})
			.catch((e: any) => {
				setSaveError(e.response.data);
			})
			.finally(() => setSavingData(false));
	};

	const HandleTemplateStatus = async (pageId: number, active: any) => {
		await updateSendsPageActiveStatus(pageId, active).then((resp) => {
			updateSendsStatus.mutate({
				newData: active,
				id: pageId,
			});
		});
	};

	const ShowDeleteConfirmation = (page: SendsPageDataType) => {
		setActiveSendsPage({ pageData: page, isOpen: true });
	};

	const HandleOpen = async (slug: string) => {
		window.open(slug, '_blank');
	};

	const HandleCopyUrl = async (slug: string) => {
		addToast('Copied to Clipboard', <FontAwesomeIcon icon={faClipboard} />);
		await navigator.clipboard.writeText(slug);
	};

	const PageActions = () => {
		return (
			<div className={'flex-row flex-gap-sm ml-a'} style={{ width: 'auto' }}>
				<Button
					onClick={(e) => [e.preventDefault(), setPageHistoryOpen(true)]}
					colour={ColourMod.Secondary}
					title={'History'}
					iconOnly={!isXL}
					icon={<FontAwesomeIcon icon={faRectangleHistory} fixedWidth={true} />}
					squared={true}
				/>
				<Button
					onClick={(e) => [e.preventDefault(), HandleOpen(data.liveUrl ? data.liveUrl : '')]}
					colour={ColourMod.Secondary}
					title={'Open'}
					iconOnly={!isXL}
					icon={<FontAwesomeIcon icon={faExternalLink} fixedWidth={true} />}
					squared={true}
				/>
				<Button
					onClick={(e) => [e.preventDefault(), HandleCopyUrl(data.liveUrl ? data.liveUrl : '')]}
					colour={ColourMod.Secondary}
					title={'Copy URL'}
					iconOnly={!isXL}
					icon={<FontAwesomeIcon icon={faCopy} fixedWidth={true} />}
					squared={true}
				/>

				<Button
					onClick={(e) => [e.preventDefault(), HandleTemplateStatus(data.id ? data.id : -1, !data?.isActive)]}
					colour={ColourMod.Secondary}
					title={'Active'}
					iconOnly={!isXL}
					icon={<FontAwesomeIcon icon={faWavePulse} fixedWidth={true} />}
					squared={true}
					addon={
						<ToggleSwitch
							extraClass={'ml-md'}
							hideLabel={true}
							type={ToggleType.Switch}
							label={data?.isActive ? 'Disable' : 'Make Active'}
							name={'pageActive'}
							checked={data?.isActive}
						/>
					}
				/>
				<Button
					onClick={(e) => [e.preventDefault(), ShowDeleteConfirmation(data)]}
					colour={ColourMod.Alert}
					title={'Delete'}
					iconOnly={!isXL}
					icon={<FontAwesomeIcon icon={faTrash} fixedWidth={true} />}
					squared={true}
				/>

				{/*
                <div onClick={(e) => e.stopPropagation()}>
                    <ToggleSwitch type={ToggleType.Switch} hideLabel={true} name={`sendsPageEntry${pageId}`} label={'Active'} checked={data.isActive} onChange={(e: any) => HandleTemplateStatus(data.id ? data.id : -1, e.currentTarget.checked)} />
                </div>*/}
			</div>
		);
	};

	return (
		<SendMailHistoryProvider pageId={parseInt(pageId as string)}>
			<FullWidthSidebar
				navigationComponent={
					<SendsAdminNavigation
						title={`Editing '${data?.title}'`}
						addonContent={
							<div className={'section-navigation-header-collapsed-addon'}>
								{isMedium && !isLarge && <PageActions />}

								{!isMedium && (
									<>
										<Button
											squared={true}
											title={'Actions'}
											extraClass={'edit-page-menu'}
											showArrow={isMedium}
											icon={<FontAwesomeIcon icon={faEllipsisVertical} />}
											iconOnly={!isMedium}
										/>

										<DropdownMenu target={['edit-page-menu']} forcedZIndexNumber={200000}>
											<ItemMenuEntry
												title={'Page History'}
												callback={() => setPageHistoryOpen(true)}
												icon={<FontAwesomeIcon icon={faRectangleHistory} fixedWidth={true} />}
											/>

											<ItemMenuEntry title={'Divider'} sectionDivider={true} />

											<ItemMenuEntry
												title={'Open'}
												callback={() => HandleOpen(data.liveUrl ? data.liveUrl : '')}
												icon={<FontAwesomeIcon icon={faExternalLink} fixedWidth={true} />}
											/>

											<ItemMenuEntry
												title={'Copy URL'}
												callback={() => HandleCopyUrl(data.liveUrl ? data.liveUrl : '')}
												icon={<FontAwesomeIcon icon={faCopy} fixedWidth={true} />}
											/>

											<ItemMenuEntry title={'Divider'} sectionDivider={true} />

											<ItemMenuEntry
												title={'Active'}
												callback={() =>
													HandleTemplateStatus(data.id ? data.id : -1, !data?.isActive)
												}
												icon={<FontAwesomeIcon icon={faWavePulse} fixedWidth={true} />}
												addonContent={
													<ToggleSwitch
														hideLabel={true}
														type={ToggleType.Switch}
														label={data?.isActive ? 'Disable' : 'Make Active'}
														name={'pageActive'}
														checked={data?.isActive}
													/>
												}
											/>

											<ItemMenuEntry
												title={'Delete'}
												callback={() => ShowDeleteConfirmation(data)}
												icon={<FontAwesomeIcon icon={faTrash} fixedWidth={true} />}
											/>
										</DropdownMenu>
									</>
								)}
							</div>
						}
					/>
				}
				extraContentClass={`flex-column `}
			>
				{isLarge && (
					<BasicPageHeader title={`Editing '${data?.title}'`}>
						<PageActions />
					</BasicPageHeader>
				)}

				<div className={'pt-xl pb-xl p-md'}>
					{status === 'loading' ? (
						<GenericLoader label={'Sends Templates'} />
					) : status === 'error' ? (
						<span>Error: {error.message}</span>
					) : (
						<div className={'row mb-xxxl'}>
							<div className={'col-xs-12 col-xl-6 col-xxl-5 '}>
								<CreateStandaloneSendForm
									saveCallback={HandleSave}
									existingData={data}
									savingData={savingData}
									error={saveError}
								/>
							</div>
						</div>
					)}

					{pageId && pageHistoryOpen && (
						<PageHistoryModal
							pageId={parseInt(pageId as string)}
							visible={pageHistoryOpen}
							closeCallback={() => setPageHistoryOpen(false)}
						/>
					)}
				</div>

				{activeSendsPage.isOpen && (
					<ConfirmDeleteSendsPageModal
						pageData={data}
						isOpen={activeSendsPage.isOpen}
						closeCallback={() => setActiveSendsPage({ ...activeSendsPage, isOpen: false })}
						actionedCallback={() => navigate('/')}
					/>
				)}
			</FullWidthSidebar>
		</SendMailHistoryProvider>
	);
};

export default EditSendsPage;
